import CollapsibleView from "@components/molecules/CollapsibleView";
import LabelView from "@components/molecules/LabelView";
import Locations from "@components/molecules/Locations/LocationModal";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, Platform } from "react-native";
import { useTheme } from "styled-components/native";
import styled, { DefaultTheme } from "styled-components/native";
import { setCompanyLocations, useUserInfo } from "@redux/slices/userInfoSlice";
import { ProductDetailProps } from "@scenes/pdp/types";
import RenderHeaderView from "@scenes/pdp/utils/HeaderView";
import { Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useDispatch, useSelector } from "react-redux";

type ShippingLocationProps = ProductDetailProps & {
  locations?: any[];
  onLocationsChange?: (locations: any[]) => void;
};

const ShippingLocation = ({
  title,
  orderCart,
  locations,
  onLocationsChange,
}: ShippingLocationProps) => {
  const [isSelectLocationOpen, setIsSelectLocationOpen] = useState(false);
  const userInfo = useUserInfo();
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const dispatch = useDispatch();
  const priceMode = useMemo(
    () => userInfo?.user?.price_mode,
    [userInfo?.user?.price_mode]
  );
  const customization = orderCart?.shipping_location;
  const usesProvidedLocations = locations !== undefined;
  const companyLocations = useSelector(
    (state: any) => state.userInfo.companyLocations
  );
  const sourceLocations = usesProvidedLocations
    ? locations ?? []
    : companyLocations || [];
  const [localSelectedLocation, setLocalSelectedLocation] = useState({
    locations: sourceLocations,
  });

  useEffect(() => {
    setLocalSelectedLocation({
      locations: usesProvidedLocations
        ? locations ?? []
        : companyLocations || [],
    });
  }, [usesProvidedLocations, locations, companyLocations]);
  const getCustomArray = () => {
    const primaryLocation = localSelectedLocation?.locations?.find(
      (loc) => loc.is_primary
    );

    if (!primaryLocation) return [];

    return customization?.shipping_location
      .filter((custom) => custom.priceMode.includes(priceMode))
      .sort((a, b) => a.order - b.order)
      .map((custom) => {
        const rawValue = primaryLocation[custom.key];
        const isEmpty = rawValue === "" || rawValue == null;
        const description = isEmpty
          ? "-"
          : String(custom.key).toLowerCase().includes("email")
          ? String(rawValue).replaceAll(";", "; ")
          : String(rawValue);

        return {
          title: custom.label,
          description,
        };
      });
  };

  const keyExtractor = (item: any, index: number) => `${item.title}-${index}`;
  const setPrimaryLocationById = (locationId: number) => {
    const currentLocations = localSelectedLocation?.locations;
    if (!currentLocations) return;

    const updatedLocations = currentLocations.map((loc) => ({
      ...loc,
      is_primary: loc.id === locationId,
    }));
    setLocalSelectedLocation({ locations: updatedLocations });
    if (usesProvidedLocations) {
      onLocationsChange?.(updatedLocations);
      return;
    }
    dispatch(setCompanyLocations([...updatedLocations]));
  };
  const getPrimaryLocation = () => {
    return (
      localSelectedLocation?.locations?.find((loc) => loc.is_primary) || null
    );
  };
  const ItemCard = ({ item }: any) => (
    <>
      {item?.showNumber === undefined || item?.showNumber === true ? (
        <CustomView>
          <LabelView
            itemLeftText={t(item.title)}
            itemRightText={item.description ?? "-"}
            ellipsize={false}
            rightPrefixTextTheme={
              item.color
                ? (theme[item.color as keyof DefaultTheme] as string)
                : theme["core-05"]
            }
            leftPostfixText=""
            itemLeftTextTheme={
              item.isTitle && item.isTitle === true
                ? theme["core-05"]
                : theme["core-06"]
            }
            isFixedKeyWidth={true}
            cellWidth={theme.isMobile ? undefined : 200}
            keyTypographyStyle={
              item.isTitle && item.isTitle === true
                ? typography["Body-M"]
                : typography["Body-R"]
            }
            valueTypographyStyle={typography["Body-M"]}
            clickableHandle={item?.clickable}
            keyWidthPercent={!theme.isLargeScreen ? 40 : undefined}
            keyWidth={theme.isLargeScreen ? 120 : undefined}
            marginRight={0}
            isFixWidth
            alignKeyTop
            valueWidthPercent={!theme.isLargeScreen ? 60 : undefined}
          />
        </CustomView>
      ) : null}
    </>
  );

  const ListHeaderComponent = () => {
    return localSelectedLocation?.locations?.length !== 1 ? (
      <>
        {RenderHeaderView(
          setIsSelectLocationOpen,
          isSelectLocationOpen,
          getPrimaryLocation(),
          localSelectedLocation?.locations,
          theme,
          t,
          typography,
          "orderCart.shippingLocation.input.text"
        )}
        <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
          {t("orderCart.shippingLocation.screenText.text")}
        </VDBText>
        <Spacer size={SpaceType.Double} />
      </>
    ) : undefined;
  };

  return (
    <>
      <CollapsibleView
        initExpanded={true}
        title={title}
        arrowStyling={{ color: theme["core-04"] }}
        applyShadow
        applyTopPadding={false}
        applyBottomPadding={false}
      >
        <Divider />
        <Container>
          <FlatList
            data={getCustomArray()}
            renderItem={({ item }) => <ItemCard item={item} />}
            keyExtractor={keyExtractor}
            scrollEnabled={Platform.OS === "web" ? true : false}
            ListHeaderComponent={ListHeaderComponent}
          />
        </Container>
      </CollapsibleView>

      <Locations
        key={`location-modal-${getPrimaryLocation()?.id}`}
        isOpen={isSelectLocationOpen}
        onClose={() => setIsSelectLocationOpen(false)}
        languages={localSelectedLocation.locations}
        selectedLocation={getPrimaryLocation()}
        changeLocation={(lan) => {
          setPrimaryLocationById(lan.id);
        }}
        title={"orderCart.shippingLocation.popUp.title.text"}
      />
    </>
  );
};

const Container = styled.View`
  padding: 16px;
`;

const CustomView = styled.View`
  justify-content: flex-start;
  margin-vertical: 8px;
`;

const Divider = styled.View`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
`;

export default ShippingLocation;
